import { ISourceDataNote, ISourceDataSubnote, NoteType } from '@/api/dtos';
import { ISourceDataFormFieldComponentProps, ISourceDataVerificationItem } from '@/utilities/forms/SourceDataFormProcessor';
import { faClipboardQuestion, faCalendarDays, faUser } from '@fortawesome/duotone-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, Dialog, Divider, IconButton, Paper, Slide, styled, Tab, Tabs, Toolbar, Typography, lighten, darken, Chip, Tooltip, alpha } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { FC, forwardRef, ReactElement, Ref, useMemo, useState, useEffect } from 'react';
import ConfigurationFormEditInfo, { ConfigurationTitle } from '../../configuration/edit/ConfigurationFormEditInfo';
import SourceDataInfo, { ISourceDataInfoProps, SourceDataBox, SourceDataTitle } from './SourceDataInfo';
import { NoteStatus } from '../../../../api/dtos';
import { DateTime } from 'luxon'
import SourceDataSubnote from './SourceDataSubnote';

const Note = styled(Paper)(({theme}) => ({
    margin: theme.spacing(2, 0, 2, 0),
    padding: theme.spacing(2, 2, 0, 2),
    background: theme.palette.mode === 'dark' ? 
        lighten(theme.palette.common.black, 0.18) :
        darken(theme.palette.common.white, 0.03)
}));

const NoteTitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
}));

const NoteStatusChip = styled(Chip)(({theme}) => ({
    marginLeft: 'auto',
}));


const NoteDateChip = styled(Chip)(({theme}) => ({
    '& svg': {
        transform: 'scale(0.75)'
    },
    marginLeft: theme.spacing(2)
}));

const NoteDividerBox = styled(Box)(({theme}) => ({
    margin: theme.spacing(0, -2)
}));

const SourceDataNote: FC<ISourceDataNote> = ({
    type,
    status,
    createdDate,
    creatorUsername,
    creator,
    notes
}) => {


    const typeLabel =  useMemo(() => {
        switch (type) {
            case NoteType.Query:
                return 'Query';
            case NoteType.ValidationFailed:
                return 'Validation Failure';
            case NoteType.Internal:
                return 'Internal';
            case NoteType.ProtocolDeviation:
                return 'Protocol Deviation';
            case NoteType.ImportantProtocolDeviation:
                return 'Important Protocol Deviation';
            case NoteType.Annotation:
                return 'Annotation';
            case NoteType.ReasonForChange:
                return 'Reason For Change';
            default:
                return null;
        }
    }, [type])

    const statusLabel =  useMemo(() => {
        switch (status) {
            case NoteStatus.New:
                return <NoteStatusChip size="small" label="New" color="error" />;
            case NoteStatus.Updated:
                return <NoteStatusChip size="small" label="Update" color="warning" />;
            case NoteStatus.ResolutionProposed:
                return <NoteStatusChip size="small" label="Resolution Proposed" color="info" />;
            case NoteStatus.Closed:
                return <NoteStatusChip size="small" label="Closed" color="success" />;
            default:
                return null;
        }
    }, [status])

    return (
        <Note>
            <NoteTitleBox>
                {typeLabel}
                <Box sx={{ml: 'auto'}}>
                    {
                        !!statusLabel && (
                            <Tooltip title="Status">
                                {statusLabel}
                            </Tooltip>
                        )
                    }
                    {
                        !!createdDate && (
                            <Tooltip title="Date Created">
                                <NoteDateChip size="small" label={DateTime.fromISO(createdDate).toFormat('dd/MM/yyyy')} icon={<FontAwesomeIcon icon={faCalendarDays} />}/>
                            </Tooltip>
                        ) 
                    }
                    {
                        !!creator && (
                            <Tooltip title="Created By">
                                <NoteDateChip size="small" label={creator} icon={<FontAwesomeIcon icon={faUser} />}/>
                            </Tooltip>
                        ) 
                    }
                </Box>
            </NoteTitleBox>
            <NoteDividerBox>
                <Divider />
            </NoteDividerBox>
            {
                notes?.map((x, i) => {
                    return (
                        <SourceDataSubnote key={i} {...x} createdDate={x.createdDate ?? createdDate} last={(i + 1) === notes.length} />
                    );
                })
            }
        </Note>
    )
};

export default SourceDataNote;